import Label from "components/Label/Label";
import React, { FC, Fragment, useEffect, useState } from "react";
import Avatar from "shared/Avatar/Avatar";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import Input from "shared/Input/Input";
import Select from "shared/Select/Select";
import Textarea from "shared/Textarea/Textarea";
import CommonLayout from "./CommonLayout";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { useEditPropertyMutation, useGetPropertyQuery, useUploadPropertyAlbumImageMutation, useUploadPropertyCoverMutation, useDeletePropertyMutation, useDuplicatePropertyMutation, useGetAmenitiesListingQuery } from "application/features/property/propertyApiSlice";
import { selectEditingProperty, setEditingProperty } from "application/features/property/propertySlice";
import { useParams, useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import { toast } from 'react-toastify';
import ButtonThird from "shared/Button/ButtonThird";
import { Dialog, Transition } from "@headlessui/react";
import { Modal } from "../../../shared/Modal/Modal";
import { useFormNavigationGuard } from "application/hooks/useFormNavigationGuard";
import UnsavedChangesModal from "components/Quasi/UnsavedChangesModal";
import NcInputNumber from "components/NcInputNumber/NcInputNumber";
import Checkbox from "shared/Checkbox/Checkbox";
import InfoTooltip from "components/Quasi/InfoTooltip/InfoTooltip";
import { property_type } from "data/property_types";
import { PropertyType } from "data/types/property";
import { PlaceAutocomplete } from "components/GoogleMaps/PlaceAutocomplete";
import type { PlaceResult } from "components/GoogleMaps/PlaceAutocomplete";
import AddressInputMiniMap from "components/GoogleMaps/AddressInputMiniMap";

export interface EditListingPageProps {
  className?: string;
}

const TRENDS = [
  {
    id: 0,
    title: "Nessuno",
    value: "",
    desc: ""
  },
  {
    id: 1,
    title: "Smart Walking",
    value: "smart walking",
    desc: "se sei appassionato di camminate e trekking, ma hai bisogno di luoghi comodi anche per lavorare",
  },
  {
    id: 2,
    title: "Turismo delle radici",
    value: "turismo delle radici",
    desc: "Ricerchi i luoghi delle tue origini, vuoi scoprire le storie autentiche, respirare e assaporare profumi e sapori di un tempo passato",
  },
  {
    id: 3,
    title: "Smart hub",
    value: "smart hub",
    desc: "cerchi spazi flessibili e adattabili alle esigenze del tuo lavoro, che sia un team retreat, una riunione in presenza o in remoto",
  },
];

const EditListingPage: FC<EditListingPageProps> = ({ className = "" }) => {
  const propertyId = useParams<{ id: string }>().id;
  const [isOpen, setIsOpen] = useState(false);
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { data: property, isLoading, isFetching } = useGetPropertyQuery(propertyId, {
    refetchOnMountOrArgChange: true,
  });

  const [updateAlbumImage, { isLoading: uploadingAlbumImage }] = useUploadPropertyAlbumImageMutation()
  const [updateCoverImage, { isLoading: uploadingCoverImage }] = useUploadPropertyCoverMutation()
  const [updateProperty, { isLoading: updatingProperty }] = useEditPropertyMutation();
  const [deleteProperty, { isLoading: isDeletingProperty }] = useDeletePropertyMutation();
  const [duplicateProperty, { isLoading: isDuplicatingProperty }] = useDuplicatePropertyMutation();
  const { data: amenitiesListing, isLoading: isLoadingAmenities } = useGetAmenitiesListingQuery({});

  const editingProperty = useSelector(selectEditingProperty);

  const dispatch = useDispatch();

  const [cover, setCover] = useState()
  const [album, setAlbum] = useState<any[]>(
    Array.isArray(editingProperty?.gallery_images) ? editingProperty.gallery_images : []
  )

  const [selectedPlace, setSelectedPlace] = useState<PlaceResult | null>({
    latitude: editingProperty?.location?.latitude || 41.9027,
    longitude: editingProperty?.location?.longitude || 12.4963,
    formatted_address: editingProperty?.address || "",
    city: editingProperty?.city || "",
    country: editingProperty?.state || "",
    postal_code: editingProperty?.postal_code || "",
    province: editingProperty?.country || "",
    region: editingProperty?.region || "",
    place_id: editingProperty?.place_id || "",
    street_number: editingProperty?.building || "",
  });

  const coverRef = React.useRef<HTMLInputElement>(null);
  const albumRef = React.useRef<HTMLInputElement>(null);
  const buildingInputRef = React.useRef<HTMLInputElement>(null);
  const cityInputRef = React.useRef<HTMLInputElement>(null);
  const countryInputRef = React.useRef<HTMLInputElement>(null);
  const postalCodeInputRef = React.useRef<HTMLInputElement>(null);

  const [propertyToSend, setPropertyToSend] = useState(editingProperty)

  const navigate = useNavigate();

  // Navigation guard for unsaved changes
  const { isDirty, setIsDirty, resetDirty, isModalOpen, handleStay, handleDiscard } = useFormNavigationGuard();

  const notify = async (success: boolean) => {
    if (success) {
      toast.success("Alloggio aggiornato con successo")
    } else {
      toast.error("Errore nell'aggiornamento dell'alloggio")
    }
  }

  const getCover = async (e: any) => {
    try {
      if (e.target.files[0].size > 10485760) {
        throw alert('Inserisci un file di dimensioni inferiori a 10mb')
      }
      setCover(e.target.files[0])
      setIsDirty(true);
    } catch (err) {
      console.log(err)
    }
  }

  const getAlbum = async (e: any) => {
    try {
      const nextFile = e.target.files?.[0]
      if (!nextFile) return
      setAlbum((prev) => {
        const safePrev = Array.isArray(prev) ? prev : []
        return [...safePrev, nextFile]
      })
      setIsDirty(true);
    } catch (err) {
      console.log(err)
    }
  }

  const uploadCover = async () => {
    if (cover) {
      const formData = new FormData()
      formData.append('property-cover', cover)
      const response: any = await updateCoverImage(formData).unwrap();
      return response.result.s3Location
    }
    return editingProperty.cover
  }

  const uploadAlbum = async () => {
    const media = []
    const files = Array.isArray(album) ? album : []
    for (const file of files) {
      if (typeof file === 'object') {
        const formData = new FormData()
        formData.append('property-album-image', file)
        const response = await updateAlbumImage(formData).unwrap();
        media.push(response.result.s3Location)
      } else {
        media.push(file)
      }
    }
    return media
  }

  const onSubmit = async () => {
    try {
      const media = await uploadAlbum();
      const cover = await uploadCover();

      const updatedProperty = { ...editingProperty, gallery_images: media, cover } as any;
      dispatch(setEditingProperty(updatedProperty))

      const toSend: any = { ...updatedProperty }
      delete toSend.host
      delete toSend.cover_url
      delete toSend.keyword

      await updateProperty(toSend).unwrap().then((payload) => {
        notify(true);
        resetDirty(); // Reset dirty flag after successful save
      })
        .catch((error) => notify(false))

    } catch (err) {
      console.log(err)
    }
  }

  const handleStop = async () => {
    try {
      await updateProperty({
        property_uuid: editingProperty.property_uuid,
        name: editingProperty.name,
        stopped: true
      }).unwrap().then((payload) => notify(true))
        .catch((error) => notify(false));
      setIsOpen(false)
      toast.success("Alloggio messo in pausa con successo")
    } catch (err) {
      toast.error("Errore nel mettere in pausa l'alloggio")
      console.log(err)
    }
  }

  const handlePublish = async () => {
    try {
      await updateProperty({
        property_uuid: editingProperty.property_uuid,
        name: editingProperty.name,
        stopped: false
      }).unwrap().then((payload) => notify(true))
        .catch((error) => notify(false));
      setIsOpen(false)
      toast.success("Alloggio pubblicato con successo")
      window.location.reload()
    } catch (err) {
      toast.error("Errore durante la pubblicazione  dell'alloggio")
      console.log(err)
    }
  }

  const handleUpdatePropertyField = (field: string, value: any) => {
    dispatch(setEditingProperty({ ...editingProperty, [field]: value }))
    setIsDirty(true);
  }

  const handleDeleteProperty = async () => {
    try {
      await deleteProperty(propertyId).unwrap();
      toast.success("Annuncio eliminato con successo");
      navigate("/host/my-properties");
    } catch (err) {
      toast.error("Errore durante l'eliminazione dell'annuncio");
      console.log(err);
    }
  };

  const extractDuplicatedPropertyId = (response) => {
    if (!response) return null;
    if (typeof response === "string") return response;
    return (
      response?.property_uuid ||
      response?.data?.property_uuid ||
      response?.property?.property_uuid ||
      response?.data?.property?.property_uuid ||
      response?.result?.property_uuid ||
      response?.result?.data?.property_uuid ||
      response?.uuid ||
      response?.data?.uuid ||
      response?.result?.uuid
    );
  };

  const handleDuplicateProperty = async () => {
    try {
      const response = await duplicateProperty(propertyId).unwrap();
      const newId = extractDuplicatedPropertyId(response);
      if (!newId) {
        throw new Error("Missing duplicated property id in response");
      }
      toast.success("Annuncio duplicato con successo");
      navigate(`/host/edit-property/${newId}`);
    } catch (err) {
      toast.error("Errore durante la duplicazione dell'annuncio");
      console.log(err);
    }
  };

  useEffect(() => {
    if (property) {
      const incomingGallery = property.property?.gallery_images
      const normalizedProperty = {
        ...property.property,
        gallery_images: Array.isArray(incomingGallery) ? incomingGallery : [],
      };
      dispatch(setEditingProperty(normalizedProperty))
      setAlbum(normalizedProperty.gallery_images)
    }
  }, [property]);

  useEffect(() => {
    // When navigating between /host/edit-property/:id routes, the component stays mounted.
    // Clear stale data only if we currently have a different listing loaded.
    if (
      editingProperty?.property_uuid &&
      propertyId &&
      String(editingProperty.property_uuid) !== String(propertyId)
    ) {
      dispatch(setEditingProperty(null));
      setAlbum([]);
      setCover(undefined);
      setIsOpen(false);
      setIsPublishModalOpen(false);
      setIsDeleteModalOpen(false);
    }
  }, [propertyId]);


  useEffect(() => {
    return () => {
      dispatch(setEditingProperty(null));
    };
  }, []);

  useEffect(() => {
    if (!selectedPlace || !editingProperty) {
      return;
    }

    const updatedProperty = {
      ...editingProperty,
      location: {
        latitude: selectedPlace.latitude,
        longitude: selectedPlace.longitude,
      },
      address: selectedPlace.formatted_address,
      city: selectedPlace.city,
      state: selectedPlace.country,
      postal_code: selectedPlace.postal_code,
      country: selectedPlace.province,
      region: selectedPlace.region,
      place_id: selectedPlace.place_id,
      building: selectedPlace.street_number,
    };

    // Automatically update the form fields based on the selected place
    if (buildingInputRef.current) {
      buildingInputRef.current.value = selectedPlace.street_number || "";
    }
    if (cityInputRef.current) {
      cityInputRef.current.value = selectedPlace.city || "";
    }
    if (countryInputRef.current) {
      countryInputRef.current.value = selectedPlace.province || "";
    }
    if (postalCodeInputRef.current) {
      postalCodeInputRef.current.value = selectedPlace.postal_code || "";
    }

    dispatch(setEditingProperty(updatedProperty));
  }, [selectedPlace]);


  const isWrongPropertyLoaded =
    !!editingProperty?.property_uuid &&
    !!propertyId &&
    String(editingProperty.property_uuid) !== String(propertyId);

  if (isLoading || isFetching || !editingProperty || isWrongPropertyLoaded) return (
    <div className={`nc-EditListingPage ${className}`} data-nc-id="EditListingPage">
      <Helmet>
        <title>Il tuo alloggio || TRIBYOU</title>
      </Helmet>
      <UnsavedChangesModal
        isOpen={isModalOpen}
        onStay={handleStay}
        onDiscard={handleDiscard}
      />
      <CommonLayout propertyId={propertyId}>
        <div className="space-y-6 sm:space-y-8">
          {/* HEADING */}
          <h2 className="text-3xl font-semibold">Informazioni alloggio</h2>
          <div className="w-14 border-b border-neutral-200 dark:border-neutral-700 è-"></div>
          <Skeleton className="w-32 h-32" count={3} />
        </div>
      </CommonLayout>
    </div>
  );
  return (
    <div className={`nc-EditListingPage ${className}`} data-nc-id="EditListingPage">
      <Helmet>
        <title>Il tuo alloggio || TRIBYOU</title>
      </Helmet>
      <UnsavedChangesModal
        isOpen={isModalOpen}
        onStay={handleStay}
        onDiscard={handleDiscard}
      />
      <CommonLayout propertyId={propertyId}>
        <div className="space-y-6 sm:space-y-8">
          {/* HEADING */}
          <h2 className="text-3xl font-semibold">Informazioni alloggio</h2>

          <div className="flex-grow mt-10 md:mt-0 max-w-3xl space-y-8">
            {/* Property Type, Rental Type, Mood, Trends */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold">Tipo di proprietà</h3>
              <div className="space-y-4">
                <div>
                  <Label>Tipo di proprietà</Label>
                  <Select
                    defaultValue={editingProperty.property_type}
                    onChange={(e) => handleUpdatePropertyField("property_type", e.target.value)}
                    className="mt-1.5"
                  >
                    {property_type.map((item: PropertyType) => (
                      <option key={item.id} value={item.value}>{item.label}</option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Label>Affitta:</Label>
                  <Select
                    defaultValue={editingProperty.rental_from}
                    onChange={(e) => handleUpdatePropertyField("rental_from", e.target.value)}
                    className="mt-1.5"
                  >
                    <option value="entire_space">Intero immobile</option>
                    <option value="private_room">Una stanza</option>
                    <option value="share_room">Una stanza condivisa</option>
                  </Select>
                </div>
                <div>
                  <Label className="inline-flex items-center">
                    Mood:
                    <InfoTooltip className="ml-2" i18nKey="translation:tooltips.accommodation_upload.mood" />
                  </Label>
                  <Select
                    defaultValue={editingProperty.mood}
                    onChange={(e) => handleUpdatePropertyField("mood", e.target.value)}
                    className="mt-1.5"
                  >
                    <option value="business">Business</option>
                    <option value="comfort">Comfort</option>
                    <option value="authentic">Authentic</option>
                  </Select>
                </div>
                <div>
                  <Label>Trends:</Label>
                  <Select
                    defaultValue={editingProperty.trend}
                    onChange={(e) => handleUpdatePropertyField("trend", e.target.value)}
                    className="mt-1.5"
                  >
                    {TRENDS.map((item) => (
                      <option key={item.id} value={item.value}>{item.title}</option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Checkbox
                    isChecked={editingProperty.only_in_package || false}
                    onChange={(e) => handleUpdatePropertyField("only_in_package", e)}
                    className="!pl-8 !pr-10"
                    label="Prenotabile solo in pacchetto"
                    name="only_in_package"
                  />
                </div>
              </div>
            </div>

            {/* Location Details */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold">Posizione</h3>
              <div className="space-y-4">
                <div>
                  <Label>Nazione</Label>
                  <Select
                    onChange={(e) => handleUpdatePropertyField("state", e.target.value)}
                    defaultValue={editingProperty.state}
                    className="mt-1.5"
                  >
                    <option value="italy">Italia</option>
                    <option value="spain">Spagna</option>
                    <option value="france">Francia</option>
                    <option value="portugal">Portogallo</option>
                    <option value="germany">Germania</option>
                  </Select>
                </div>
                <div>
                  <Label>Indirizzo</Label>
                  <div className="mt-1.5">
                    <PlaceAutocomplete
                      onPlaceSelect={setSelectedPlace}
                      placeholder="Cerca un indirizzo"
                    />
                  </div>
                </div>
                <div>
                  <Label>Piano, Interno, Palazzina (facoltativo)</Label>
                  <Input
                    onChange={(e) => handleUpdatePropertyField("building", e.target.value)}
                    defaultValue={editingProperty.building}
                    ref={buildingInputRef}
                    className="mt-1.5"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Città</Label>
                    <Input
                      onChange={(e) => handleUpdatePropertyField("city", e.target.value)}
                      defaultValue={editingProperty.city}
                      ref={cityInputRef}
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label>Provincia</Label>
                    <Input
                      onChange={(e) => handleUpdatePropertyField("country", e.target.value)}
                      defaultValue={editingProperty.country}
                      ref={countryInputRef}
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label>CAP</Label>
                    <Input
                      onChange={(e) => handleUpdatePropertyField("postal_code", e.target.value)}
                      defaultValue={editingProperty.postal_code}
                      ref={postalCodeInputRef}
                      className="mt-1.5"
                    />
                  </div>
                </div>
                {selectedPlace && (
                  <div className="mt-4">
                    <AddressInputMiniMap
                      defaultCenter={{ lat: selectedPlace.latitude, lng: selectedPlace.longitude }}
                      place={selectedPlace}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Property Details */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold">Dettagli proprietà</h3>
              <div className="space-y-4">
                <div>
                  <Label className="inline-flex items-center">
                    Media (m2)
                    <InfoTooltip className="ml-2" i18nKey="translation:tooltips.accommodation_upload.details.media_m2" />
                  </Label>
                  <Select
                    onChange={(e) => handleUpdatePropertyField("square_meters", Number(e.target.value))}
                    defaultValue={editingProperty.square_meters}
                    className="mt-1.5"
                  >
                    <option value="50">50(m2) o meno</option>
                    <option value="100">50(m2)-100(m2)</option>
                    <option value="200">100(m2)-200(m2)</option>
                    <option value="300">200(m2) o più</option>
                  </Select>
                </div>
                <NcInputNumber
                  labelNode={
                    <span className="inline-flex items-center">
                      Ospiti
                      <InfoTooltip className="ml-2" i18nKey="translation:tooltips.accommodation_upload.details.ospiti" />
                    </span>
                  }
                  defaultValue={editingProperty.guests_included || 2}
                  min={1}
                  onChange={(value) => handleUpdatePropertyField("guests_included", Number(value))}
                  className="mt-1.5"
                />
                <NcInputNumber
                  labelNode={
                    <span className="inline-flex items-center">
                      Stanze
                      <InfoTooltip className="ml-2" i18nKey="translation:tooltips.accommodation_upload.details.stanze_da_letto" />
                    </span>
                  }
                  defaultValue={editingProperty.bedrooms || 1}
                  min={1}
                  onChange={(value) => handleUpdatePropertyField("bedrooms", Number(value))}
                  className="mt-1.5"
                />
                <NcInputNumber
                  label="Letti"
                  defaultValue={editingProperty.beds || 1}
                  min={1}
                  onChange={(value) => handleUpdatePropertyField("beds", Number(value))}
                  className="mt-1.5"
                />
                <NcInputNumber
                  labelNode={
                    <span className="inline-flex items-center">
                      Bagni
                      <InfoTooltip className="ml-2" i18nKey="translation:tooltips.accommodation_upload.details.bagni" />
                    </span>
                  }
                  defaultValue={editingProperty.bathrooms || 1}
                  onChange={(value) => handleUpdatePropertyField("bathrooms", Number(value))}
                  className="mt-1.5"
                />
                <NcInputNumber
                  labelNode={
                    <span className="inline-flex items-center">
                      Cucine
                      <InfoTooltip className="ml-2" i18nKey="translation:tooltips.accommodation_upload.details.cucine" />
                    </span>
                  }
                  defaultValue={editingProperty.kitchen || 1}
                  onChange={(value) => handleUpdatePropertyField("kitchen", Number(value))}
                  className="mt-1.5"
                />
              </div>
            </div>

            {/* Description and Details */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold">Descrizione e dettagli</h3>
              <div className="space-y-4">
                <div>
                  <Label>Nome</Label>
                  <Input onChange={(e) => handleUpdatePropertyField("name", e.target.value)} className="mt-1.5" defaultValue={editingProperty.name} />
                </div>
                <div>
                  <Label>Descrizione</Label>
                  <Textarea onChange={(e) => handleUpdatePropertyField("description", e.target.value)} className="mt-1.5" defaultValue={editingProperty.description} rows={10} maxLength={5000} />
                </div>
                <div>
                  <Label className="inline-flex items-center">
                    Scheda tecnica
                    <InfoTooltip className="ml-2" i18nKey="translation:tooltips.accommodation_upload.tech_sheet" />
                  </Label>
                  <Textarea
                    onChange={(e) => handleUpdatePropertyField("item_detail", e.target.value)}
                    className="mt-1.5"
                    defaultValue={editingProperty.item_detail}
                    placeholder="Inserisci la scheda tecnica della struttura..."
                    rows={8}
                  />
                </div>
                <div>
                  <Label>Cusr</Label>
                  <Input onChange={(e) => handleUpdatePropertyField("cusr", e.target.value)} className="mt-1.5" defaultValue={editingProperty.cusr} />
                </div>
                <div>
                  <Label>Prezzo base a notte (Lunedì-Giovedì) </Label>
                  <Input onChange={(e) => handleUpdatePropertyField("weekday_price", e.target.value)} className="mt-1.5" defaultValue={editingProperty.weekday_price} type="number" />
                </div>
                <div>
                  <Label>Prezzo base a notte (Venerd-Domenica) </Label>
                  <Input onChange={(e) => handleUpdatePropertyField("weekend_price", e.target.value)} className="mt-1.5" defaultValue={editingProperty.weekend_price} type="number" />
                </div>
                <div>
                  <Label>Spese di pulizia</Label>
                  <Input onChange={(e) => handleUpdatePropertyField("cleaning_fee", e.target.value)} className="mt-1.5" defaultValue={editingProperty.cleaning_fee} type="number" />
                </div>
              </div>
            </div>

            {/* Amenities Section */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold">Servizi</h3>
              {!isLoadingAmenities && amenitiesListing && (
                <div className="space-y-6">
                  {amenitiesListing.map((item: any) => (
                    <div key={item.category}>
                      <label className="text-base font-medium" htmlFor="">
                        {item.category}
                      </label>
                      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {item.amenities.map((amenity: any) => (
                          <Checkbox
                            key={amenity}
                            label={amenity}
                            name={amenity}
                            isChecked={
                              Array.isArray(editingProperty.amenities) &&
                              editingProperty.amenities.includes(amenity)
                            }
                            onChange={(e) => {
                              const currentAmenities = Array.isArray(editingProperty.amenities)
                                ? editingProperty.amenities
                                : [];
                              let updatedAmenities;
                              if (e) {
                                if (!currentAmenities.includes(amenity)) {
                                  updatedAmenities = [...currentAmenities, amenity];
                                } else {
                                  updatedAmenities = currentAmenities;
                                }
                              } else {
                                updatedAmenities = currentAmenities.filter((a: string) => a !== amenity);
                              }
                              handleUpdatePropertyField("amenities", updatedAmenities);
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Media Section */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold">Immagini</h3>
              <div>
                <Label>Immagine di copertina</Label>
                <div className="mt-1.5 flex items-start flex-col">
                  {cover ?
                    <img src={URL.createObjectURL(cover)} className="h-auto w-full md:w-40" />
                    :
                    <div>
                      <input type="file" ref={coverRef} accept="image/*" onChange={getCover} className="opacity-0 h-0 w-0 absolute" />
                      <img src={editingProperty.cover} className="h-auto w-full md:w-40" />
                    </div>
                  }
                  <div className="mt-5">
                    <ButtonThird onClick={() => coverRef.current?.click()}>Carica nuova immagine</ButtonThird>
                  </div>
                </div>
              </div>
              <div>
                <Label>Galleria annuncio</Label>
                <div className="grid grid-cols-3 gap-4 mt-4">
                {album && album.length > 0 ?
                  album.map((image: any, index) => (
                    <div key={index} className="relative">
                      <img src={image.length ? image : URL.createObjectURL(image)} alt="" className="w-full h-32 object-cover rounded-md" />
                      <div className="absolute top-0 right-0">
                        <button
                          onClick={() => {
                            setAlbum(album.filter((item, i) => i !== index))
                            setIsDirty(true);
                          }}
                          className="bg-neutral-100 dark:bg-neutral-800 rounded-full p-1"
                        >
                          <svg
                            className="w-4 h-4 text-neutral-6000 dark:text-neutral-300"
                            stroke="currentColor"
                            fill="none"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M6 18L18 6M6 6l12 12"
                            ></path>
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))
                  :
                  null
                }
                </div>
              </div>
              <div className="mt-5">
                <input type="file" ref={albumRef} multiple accept="image/*" onChange={getAlbum} className="opacity-0 h-0 w-0 absolute" />
                <ButtonThird onClick={() => albumRef.current?.click()}>Carica nuova immagine</ButtonThird>
              </div>
            </div>

            <div className="pt-2 gap-3 flex flex-row">
              {
                editingProperty.stopped ?
                  <>
                    <ButtonPrimary onClick={() => setIsPublishModalOpen(true)}>Pubblica annuncio</ButtonPrimary>
                    <ButtonSecondary
                      onClick={handleDuplicateProperty}
                      loading={isDuplicatingProperty}
                      disabled={isDuplicatingProperty}
                    >
                      Duplica annuncio
                    </ButtonSecondary>
                  </>
                  :
                  <>
                    <ButtonPrimary loading={isLoading || uploadingAlbumImage || uploadingCoverImage || updatingProperty} onClick={onSubmit}>
                      {
                        isLoading ? "Carico i dati..." :
                          uploadingAlbumImage ? "Carico la galleria..." :
                            uploadingCoverImage ? "Carico la copertina..." :
                              updatingProperty ? "Salvo i dati..." : "Salva i dati"
                      }
                    </ButtonPrimary>
                    <ButtonSecondary onClick={() => setIsOpen(true)}>Metti in pausa</ButtonSecondary>
                    <ButtonSecondary
                      onClick={handleDuplicateProperty}
                      loading={isDuplicatingProperty}
                      disabled={isDuplicatingProperty}
                    >
                      Duplica annuncio
                    </ButtonSecondary>
                    <ButtonSecondary
                      onClick={() => setIsDeleteModalOpen(true)}
                      className="!bg-red-50 hover:!bg-red-100 !text-red-600"
                    >
                      Elimina annuncio
                    </ButtonSecondary>
                  </>
              }
            </div>
          </div>
        </div>
        <Transition.Root show={isOpen} as={Fragment}>
          <Dialog as="div" onClose={() => null}>
            <Transition.Child
              as={Fragment}
              enter="transition-opacity duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
            </Transition.Child>

            <Transition.Child
              as={Fragment}
              enter="transition ease-out duration-300"
              enterFrom="transform scale-95 opacity-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition ease-in duration-200"
              leaveFrom="transform scale-100 opacity-100"
              leaveTo="transform scale-95 opacity-0"
            >
              <div className="fixed inset-0 flex items-center justify-center p-5 md:p-0">
                <div className="bg-white rounded-lg p-10 flex items-start flex-col justify-center">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    Metti in pausa l'annuncio
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 break-words max-w-md">
                      Sei sicuro di voler mettere in pausa l'annuncio? L'annuncio non sarà più visibile agli utenti.
                    </p>
                  </div>
                  <div className="mt-4 flex flex-row gap-3">
                    <ButtonSecondary
                      onClick={() => setIsOpen(false)}
                    >
                      Chiudi
                    </ButtonSecondary>

                    <ButtonPrimary
                      onClick={() => handleStop()}
                      loading={isLoading}
                    >
                      Metti in pausa
                    </ButtonPrimary>
                  </div>
                </div>
              </div>
            </Transition.Child>
          </Dialog>
        </Transition.Root>
        <Transition.Root show={isPublishModalOpen} as={Fragment}>
          <Dialog as="div" onClose={() => null}>
            <Transition.Child
              as={Fragment}
              enter="transition-opacity duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
            </Transition.Child>

            <Transition.Child
              as={Fragment}
              enter="transition ease-out duration-300"
              enterFrom="transform scale-95 opacity-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition ease-in duration-200"
              leaveFrom="transform scale-100 opacity-100"
              leaveTo="transform scale-95 opacity-0"
            >
              <div className="fixed inset-0 flex items-center justify-center p-5 md:p-0">
                <div className="bg-white rounded-lg p-10 flex items-start flex-col justify-center">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    Pubblica l'annuncio
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 break-words max-w-md">
                      Sei sicuro di voler pubblicare l'annuncio? L'annuncio sarà visibile agli utenti.
                    </p>
                  </div>
                  <div className="mt-4 flex flex-row gap-3">
                    <ButtonSecondary
                      onClick={() => setIsPublishModalOpen(false)}
                    >
                      Chiudi
                    </ButtonSecondary>

                    <ButtonPrimary
                      onClick={() => handlePublish()}
                      loading={isLoading}
                    >
                      pubblica
                    </ButtonPrimary>
                  </div>
                </div>
              </div>
            </Transition.Child>
          </Dialog>
        </Transition.Root>

        <Modal
          show={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
        >
          <div className="p-5">
            <h3 className="text-2xl font-semibold">Conferma eliminazione</h3>
            <span className="block mt-2 text-neutral-500 dark:text-neutral-400">
              Sei sicuro di voler eliminare questo annuncio? L'operazione non può essere annullata.
            </span>
            <div className="flex justify-end space-x-3 mt-8">
              <ButtonSecondary onClick={() => setIsDeleteModalOpen(false)}>
                Annulla
              </ButtonSecondary>
              <ButtonPrimary
                onClick={handleDeleteProperty}
                loading={isDeletingProperty}
                className="!bg-red-600 hover:!bg-red-700"
              >
                Elimina
              </ButtonPrimary>
            </div>
          </div>
        </Modal>

      </CommonLayout>
    </div>
  );
};

export default EditListingPage;
